import { zodResolver } from "@hookform/resolvers/zod";

import { Loader2, Trash, Upload } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  type CompanyDto,
  createCompanyDtoSchema,
  type TagDto,
} from "slde-api/dto-schemas";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { useResources } from "@/providers/resources.provider";
import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { companiesApi } from "@/services/companies/companies.api";
import { toast } from "sonner";
import { $fetch } from "@/lib/fetch";

interface CompanyFormProps {
  defaultValues?: CompanyDto & {
    categories?: TagDto[];
    logoUrl?: string | null;
  };
  onSubmit: (values: CompanyDto) => void;
  companyId?: number;
  disabled?: boolean;
  loading?: boolean;
}
export function CompanyForm({
  onSubmit: onSubmitProps,
  defaultValues,
  companyId,
  disabled,
  loading,
}: CompanyFormProps) {
  const { tags, createTag, isCreatingTag, deleteTag, isDeletingTag } =
    useResources();
  const [tagInput, setTagInput] = React.useState("");
  const tagsAnchor = useComboboxAnchor();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const [logoUrl, setLogoUrl] = React.useState<string | null>(
    defaultValues?.logoUrl ?? null,
  );

  const form = useForm<CompanyDto>({
    resolver: zodResolver(createCompanyDtoSchema),
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          tagIds: defaultValues.categories?.map((cat: TagDto) => cat.id),
        }
      : {},
  });

  const { mutate: uploadLogo, isPending: isUploadingLogo } = useMutation<
    { logoUrl: string },
    Error,
    File
  >({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("id", String(companyId));
      formData.append("type", "company-logo");

      return $fetch<{ logoUrl: string }>(companiesApi.uploads.route(), {
        method: "POST",
        body: formData,
      });
    },
    onSuccess: ({ logoUrl: newUrl }) => {
      setLogoUrl(newUrl);
      void queryClient.invalidateQueries({
        queryKey: companiesApi.companies.queryKey(),
      });
    },
    onError: () => {
      toast.error("Nie udało się przesłać logo");
    },
  });

  const { mutate: removeLogo, isPending: isRemovingLogo } = useMutation({
    mutationFn: async () => {
      return $fetch(companiesApi.uploads.deleteRoute("company-logo", companyId!), {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      setLogoUrl(null);
      void queryClient.invalidateQueries({
        queryKey: companiesApi.companies.queryKey(),
      });
    },
    onError: () => {
      toast.error("Nie udało się usunąć logo");
    },
  });

  function onSubmit(values: CompanyDto) {
    onSubmitProps(values);
  }

  return (
    <div className="flex gap-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Nazwa</FormLabel>
                  <FormControl>
                    <Input value={value ?? ""} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="standNumber"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Numer stoiska</FormLabel>
                  <FormControl>
                    <Input value={value ?? ""} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactDetails"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Opis</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Średnia sala, pomieści 200 osób"
                      value={value ?? ""}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pin"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>PIN</FormLabel>
                  <FormControl>
                    <Input value={value ?? ""} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="linkUrl"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Link</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com"
                      value={value ?? ""}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="order"
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem>
                  <FormLabel>Kolejność</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(
                          e.target.value === "" ? null : Number(e.target.value),
                        )
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="youngDoctorsSupport"
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem className="flex flex-row-reverse">
                  <FormLabel>Wystawca wspiera program 'Młody Lekarz'</FormLabel>
                  <FormControl>
                    <Checkbox
                      checked={value ?? false}
                      onCheckedChange={(val) => onChange(val)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tagIds"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Kategorie</FormLabel>
                  <FormControl>
                    <Combobox
                      items={tags}
                      multiple
                      value={value ?? []}
                      onValueChange={onChange}
                      autoHighlight
                    >
                      <ComboboxChips ref={tagsAnchor} className="w-full">
                        <ComboboxValue>
                          {(values) => (
                            <React.Fragment>
                              {tags
                                .filter((tag) => values?.includes(tag.id))
                                .map((tag: TagDto) => (
                                  <ComboboxChip key={tag.id}>
                                    {tag.name}
                                  </ComboboxChip>
                                ))}
                              <ComboboxChipsInput
                                onChange={(e) => setTagInput(e.target.value)}
                              />
                            </React.Fragment>
                          )}
                        </ComboboxValue>
                      </ComboboxChips>
                      <ComboboxContent anchor={tagsAnchor}>
                        <ComboboxEmpty>
                          <div className="flex flex-col items-center gap-2">
                            <span>Brak wyników</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isCreatingTag || !tagInput}
                              onClick={() => createTag({ name: tagInput })}
                            >
                              {isCreatingTag && (
                                <Loader2 className="animate-spin" />
                              )}
                              Dodaj &ldquo;{tagInput}&rdquo;
                            </Button>
                          </div>
                        </ComboboxEmpty>
                        <ComboboxList>
                          {(tag: TagDto) => (
                            <ComboboxItem key={tag.id} value={tag.id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{tag.name}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="link"
                                  disabled={isDeletingTag}
                                  onClick={() => deleteTag({ id: tag.id })}
                                >
                                  {isDeletingTag && (
                                    <Loader2 className="animate-spin" />
                                  )}
                                  <Trash />
                                </Button>
                              </div>
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {companyId !== undefined && (
              <div className="space-y-2">
                <FormLabel>Logo</FormLabel>
                {logoUrl ? (
                  <div className="flex flex-col gap-2">
                    <img
                      src={logoUrl}
                      alt="Logo wystawcy"
                      className="h-20 w-auto object-contain rounded border"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUploadingLogo || isRemovingLogo}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {isUploadingLogo ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Upload />
                        )}
                        Zmień logo
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={isUploadingLogo || isRemovingLogo}
                        onClick={() => removeLogo()}
                      >
                        {isRemovingLogo ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Trash />
                        )}
                        Usuń logo
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploadingLogo}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Upload />
                    )}
                    Prześlij logo
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadLogo(file);
                    e.target.value = "";
                  }}
                />
              </div>
            )}
          </div>
          <Button type="submit" disabled={disabled || loading}>
            {!!defaultValues ? "Edytuj" : "Dodaj"}
            {loading && <Loader2 className="animate-spin" />}
          </Button>
        </form>
      </Form>
    </div>
  );
}
